import { useState, useEffect } from 'react';

/**
 * Custom hook for ROAS Distribution by Channel
 * Tracks ad spend, revenue, and ROAS across different marketing channels
 */
export const useROASDistribution = (dateRange) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Mock data for different marketing channels
      const mockData = {
        channels: [
          {
            name: 'Paid - Meta',
            category: 'paid',
            adSpend: 18000,
            revenue: 95000,
            roas: 5.28,
            leads: 72,
            conversions: 8,
            cpa: 2250,
            cpl: 250,
          },
          {
            name: 'Paid - AdWords',
            category: 'paid',
            adSpend: 22000,
            revenue: 84000,
            roas: 3.82,
            leads: 65,
            conversions: 7,
            cpa: 3143,
            cpl: 338,
          },
          {
            name: 'Organic - SEO',
            category: 'organic',
            adSpend: 5000, // Content/tools cost
            revenue: 48000,
            roas: 9.6,
            leads: 35,
            conversions: 4,
            cpa: 1250,
            cpl: 143,
          },
          {
            name: 'Email - LinkedIn',
            category: 'email',
            adSpend: 3000,
            revenue: 24000,
            roas: 8.0,
            leads: 18,
            conversions: 2,
            cpa: 1500,
            cpl: 167,
          },
          {
            name: 'Paid - LinkedIn',
            category: 'paid',
            adSpend: 8000,
            revenue: 36000,
            roas: 4.5,
            leads: 22,
            conversions: 3,
            cpa: 2667,
            cpl: 364,
          },
          {
            name: 'Referral',
            category: 'organic',
            adSpend: 2000,
            revenue: 36000,
            roas: 18.0,
            leads: 12,
            conversions: 3,
            cpa: 667,
            cpl: 167,
          },
        ],
        summary: {
          totalAdSpend: 58000,
          totalRevenue: 323000,
          averageROAS: 5.57,
          totalLeads: 224,
          totalConversions: 27,
          overallCPA: 2148,
          overallCPL: 259,
        },
        monthlyTrend: [
          { month: 'Jan', adSpend: 4500, revenue: 24000, roas: 5.33 },
          { month: 'Feb', adSpend: 4800, revenue: 26000, roas: 5.42 },
          { month: 'Mar', adSpend: 5200, revenue: 29000, roas: 5.58 },
          { month: 'Apr', adSpend: 4900, revenue: 27000, roas: 5.51 },
          { month: 'May', adSpend: 5100, revenue: 28500, roas: 5.59 },
          { month: 'Jun', adSpend: 4700, revenue: 26000, roas: 5.53 },
          { month: 'Jul', adSpend: 4600, revenue: 25000, roas: 5.43 },
          { month: 'Aug', adSpend: 4800, revenue: 27000, roas: 5.63 },
          { month: 'Sep', adSpend: 5000, revenue: 28000, roas: 5.60 },
          { month: 'Oct', adSpend: 5200, revenue: 30000, roas: 5.77 },
          { month: 'Nov', adSpend: 4800, revenue: 27500, roas: 5.73 },
          { month: 'Dec', adSpend: 4600, revenue: 25000, roas: 5.43 },
        ],
      };

      setData(mockData);
    } catch (err) {
      setError(err.message || 'Failed to fetch ROAS distribution data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const refetch = () => fetchData(true);

  return {
    data,
    loading,
    refreshing,
    error,
    refetch,
  };
};